/**
 * Pure-function annotation pre-processing for the Markdown component.
 *
 * Extracted from `Markdown.tsx` so unit tests can import without pulling in
 * `react-markdown` (which is published as ESM-only and Jest can't transform
 * without extra config).
 */

/** Returns true if the character at `index` in `text` is inside a [[...]] citation marker. */
function isInsideCitationLink(text: string, index: number): boolean {
  const openPos = text.lastIndexOf("[[", index);
  if (openPos === -1) return false;
  const closePos = text.indexOf("]]", openPos + 2);
  return closePos === -1 || closePos >= index;
}

/**
 * Find the last occurrence of `pattern` in `text` that is NOT inside a [[...]] citation link.
 * Searching backwards avoids matching annotation text that appears inside citation markers
 * (e.g. "31" inside "[[warehouse:2/31329:NAME]]"), which would break the citation.
 */
function findLastIndexOutsideCitations(text: string, pattern: string): number {
  let pos = text.length - 1;
  while (pos >= 0) {
    const idx = text.lastIndexOf(pattern, pos);
    if (idx === -1) return -1;
    if (!isInsideCitationLink(text, idx)) return idx;
    pos = idx - 1;
  }
  return -1;
}

/**
 * Returns true if `index` in `text` falls inside an inline-code span (delimited by
 * single backticks on the same line). Inline code doesn't cross line boundaries,
 * so we count backticks between the start of the current line and `index` — an odd
 * count means we're inside an open code span.
 */
function isInsideInlineCode(text: string, index: number): boolean {
  const lineStart = text.lastIndexOf("\n", index - 1) + 1;
  let count = 0;
  for (let i = lineStart; i < index; i++) {
    if (text[i] === "`") count++;
  }
  return count % 2 === 1;
}

/**
 * Build a markdown-escaped variant of `text` by prefixing each markdown-special
 * char with `\`. When the LLM writes `\$4,401.62` (escaping `$` for display),
 * the raw annotation text is `$4,401.62` and won't match the source directly —
 * we search for both the original and this escaped variant.
 */
function toMarkdownEscaped(text: string): string {
  return text.replace(/([$*_`~])/g, "\\$1");
}

/**
 * Process annotation codeblocks in the markdown content.
 *
 * For ai_N annotations: find the annotation's data.text in the content immediately
 * before the codeblock (searching for bold "**text**", italic "*text*", or plain text)
 * and replace that occurrence with an annotation-trigger span so the text itself
 * becomes the clickable popover trigger — no duplicate badge rendered separately.
 *
 * For table_N / chart_N: keep as fenced code block with a blank line before it so
 * the pre handler renders a ProvenanceBadge block element.
 */
export function processAnnotations(content: string): string {
  const annotationRegex = /\n*```annotation\n([\s\S]*?)\n```/g;
  let result = "";
  let lastIndex = 0;
  let match;

  while ((match = annotationRegex.exec(content)) !== null) {
    const json = match[1];
    const matchStart = match.index;
    const matchEnd = match.index + match[0].length;

    try {
      const parsed = JSON.parse(json.trim());
      const label: string = parsed.label ?? "";

      if (label.startsWith("table_") || label.startsWith("chart_")) {
        result += content.slice(lastIndex, matchStart) + `\n\n\`\`\`annotation\n${json}\n\`\`\``;
        lastIndex = matchEnd;
        continue;
      }

      const data = parsed.data ?? {};
      const annotationText: string = data.text ?? "";
      const beforeMatch = content.slice(lastIndex, matchStart);
      // The backend inserts the block right after the annotated text, which can be inside
      // a markdown table row. The row is read from the output so far, so the second and
      // later blocks on the same row are recognised too.
      const precedingText = result + beforeMatch;
      const lineStart = precedingText.lastIndexOf("\n") + 1;
      const inTableRow = precedingText.slice(lineStart).trimStart().startsWith("|");
      // Annotation text containing `|` there spans several cells. No span can wrap it, so it
      // falls back to the inline marker, emitted without its text so the marker badge shows
      // the tier symbol instead of repeating the row.
      const spansTableCells = inTableRow && annotationText.includes("|");
      const markerJson = spansTableCells
        ? JSON.stringify({ ...parsed, data: { ...data, text: "" } })
        : json.trim();
      // `|` is entity-encoded so a span inside a markdown table row cannot split its cells.
      const escapedJson = markerJson
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/\|/g, "&#124;");

      // If the annotation block itself is inside an unclosed [[...]] citation marker,
      // skip it entirely so the citation text rejoins seamlessly. Inserting any span
      // here would break the citation and cause processCitations to double-escape the
      // span's data-annotation attribute as visible label text.
      // e.g. "[[warehouse:2/31\n```annotation...```\n329:WH]]" → "[[warehouse:2/31329:WH]]"
      const prefixUpToMatch = content.slice(0, matchStart);
      if (
        prefixUpToMatch.length > 0 &&
        isInsideCitationLink(prefixUpToMatch, prefixUpToMatch.length - 1)
      ) {
        result += beforeMatch;
        // Consume the trailing \n (if any) to close the gap between the split parts.
        lastIndex = matchEnd + (matchEnd < content.length && content[matchEnd] === "\n" ? 1 : 0);
        continue;
      }

      let replaced = false;

      if (annotationText && !spansTableCells) {
        // Try bold (**text**), then italic (*text*), then plain text — last occurrence wins.
        // For each style, also try a markdown-escaped variant (e.g. `\$4,401.62` when the
        // annotation text is `$4,401.62`), since the LLM often escapes `$`/`*`/`_` in output.
        // Skip matches inside [[...]] citation markers to avoid breaking citation links.
        const escapedText = toMarkdownEscaped(annotationText);
        const hasEscape = escapedText !== annotationText;

        type Candidate = { pattern: string; wrap: "strong" | "em" | null };
        const styledCandidates: Candidate[] = [
          { pattern: `**${annotationText}**`, wrap: "strong" },
          ...(hasEscape ? [{ pattern: `**${escapedText}**`, wrap: "strong" as const }] : []),
          { pattern: `*${annotationText}*`, wrap: "em" },
          ...(hasEscape ? [{ pattern: `*${escapedText}*`, wrap: "em" as const }] : []),
        ];

        let styledMatch: { start: number; length: number; wrap: "strong" | "em" | null } | null =
          null;
        for (const { pattern, wrap } of styledCandidates) {
          const idx = findLastIndexOutsideCitations(beforeMatch, pattern);
          if (idx !== -1) {
            styledMatch = { start: idx, length: pattern.length, wrap };
            break;
          }
        }

        if (styledMatch) {
          const inner = styledMatch.wrap
            ? `<${styledMatch.wrap}>${annotationText}</${styledMatch.wrap}>`
            : annotationText;
          result +=
            beforeMatch.slice(0, styledMatch.start) +
            `<span class="annotation-trigger" data-annotation="${escapedJson}">${inner}</span>` +
            beforeMatch.slice(styledMatch.start + styledMatch.length);
          replaced = true;
        } else {
          // Plain-text match — try the escaped variant first so `\$` boundaries are consumed
          // together with the text (otherwise we'd splice a span between `\` and the text
          // and produce broken markdown like `**\<span>...</span>**`).
          const plainCandidates = hasEscape ? [escapedText, annotationText] : [annotationText];
          let plainMatch: { start: number; length: number } | null = null;
          for (const candidate of plainCandidates) {
            const idx = findLastIndexOutsideCitations(beforeMatch, candidate);
            if (idx !== -1) {
              plainMatch = { start: idx, length: candidate.length };
              break;
            }
          }

          if (plainMatch) {
            const { start: plainIdx, length: plainLen } = plainMatch;

            // Case A: match is wrapped exactly by adjacent backticks — consume them and
            // render <code> inside the span so we preserve monospace styling and keep the
            // span OUTSIDE the code (react-markdown would treat span-inside-backticks as
            // literal text).
            const wrappedInCode =
              plainIdx > 0 &&
              beforeMatch[plainIdx - 1] === "`" &&
              beforeMatch[plainIdx + plainLen] === "`";

            if (wrappedInCode) {
              result +=
                beforeMatch.slice(0, plainIdx - 1) +
                `<span class="annotation-trigger" data-annotation="${escapedJson}"><code>${annotationText}</code></span>` +
                beforeMatch.slice(plainIdx + plainLen + 1);
              replaced = true;
            } else if (isInsideInlineCode(beforeMatch, plainIdx)) {
              // Case B: match is inside a larger inline-code span (e.g.
              // `termination_code = RUN_EXECUTION_ERROR` — backticks are on the outer
              // boundary, not adjacent to the annotation text). Splicing the span
              // between the backticks would break react-markdown; instead, consume the
              // whole code span and wrap it as the trigger.
              const lineStart = beforeMatch.lastIndexOf("\n", plainIdx - 1) + 1;
              const openBacktick = beforeMatch.lastIndexOf("`", plainIdx - 1);
              const closeBacktick = beforeMatch.indexOf("`", plainIdx + plainLen);
              const lineEnd = beforeMatch.indexOf("\n", plainIdx);
              const closeOnSameLine =
                closeBacktick !== -1 && (lineEnd === -1 || closeBacktick < lineEnd);

              if (openBacktick >= lineStart && closeOnSameLine) {
                const codeContent = beforeMatch.slice(openBacktick + 1, closeBacktick);
                result +=
                  beforeMatch.slice(0, openBacktick) +
                  `<span class="annotation-trigger" data-annotation="${escapedJson}"><code>${codeContent}</code></span>` +
                  beforeMatch.slice(closeBacktick + 1);
                replaced = true;
              }
              // Else: unbalanced backticks — fall through to invisible marker below.
            } else {
              result +=
                beforeMatch.slice(0, plainIdx) +
                `<span class="annotation-trigger" data-annotation="${escapedJson}">${annotationText}</span>` +
                beforeMatch.slice(plainIdx + plainLen);
              replaced = true;
            }
          }
        }
      }

      if (!replaced) {
        const marker = `<span class="annotation-inline" data-annotation="${escapedJson}"></span>`;
        // At the end of a table row the marker goes inside the last cell: after the closing
        // (unescaped) `|` the table parser would drop it as an extra cell.
        const closingPipe = inTableRow ? /(^|[^\\])\|[ \t]*$/.exec(beforeMatch) : null;
        const pipeIndex = closingPipe ? closingPipe.index + closingPipe[1].length : -1;
        result +=
          pipeIndex !== -1
            ? beforeMatch.slice(0, pipeIndex) + marker + beforeMatch.slice(pipeIndex)
            : beforeMatch + marker;
      }

      // Consume the trailing \n after the annotation codeblock so continuation
      // text stays inline. Inside a table row it is always consumed so the row keeps
      // its own line ending. Elsewhere, skip consumption if the next line starts a
      // block-level element (#, >, -, *, +, ordered list digit).
      if (inTableRow && content[matchEnd] === "\n") {
        lastIndex = matchEnd + 1;
      } else if (
        matchEnd < content.length &&
        content[matchEnd] === "\n" &&
        content[matchEnd + 1] !== "\n"
      ) {
        const nextChar = content[matchEnd + 1];
        const isBlockStart =
          nextChar === "#" ||
          nextChar === ">" ||
          nextChar === "-" ||
          nextChar === "*" ||
          nextChar === "+" ||
          (nextChar >= "0" && nextChar <= "9");
        lastIndex = isBlockStart ? matchEnd : matchEnd + 1;
      } else {
        lastIndex = matchEnd;
      }
    } catch {
      result += content.slice(lastIndex, matchEnd);
      lastIndex = matchEnd;
    }
  }

  result += content.slice(lastIndex);
  return result;
}
